import { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { hideModal } from "actions/modal";
import { getTags } from "selectors/mediaLibrarySelectors";
import { applyBulkTags } from "actions/mediaLibrary";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CreatableSelect,
  CreatableOption,
} from "@/components/ui/creatable-select";

interface Asset {
  _id?: string;
  asset_id?: string;
}

interface TagOption {
  value: string;
  label: string;
}

interface BulkAssetTagsModalProps {
  assets: Asset[];
}

const BulkAssetTagsModal = ({ assets }: BulkAssetTagsModalProps) => {
  const dispatch = useDispatch();
  const allTags = useSelector(getTags) as TagOption[];
  const [tags, setTags] = useState<CreatableOption[]>([]);

  const handleClose = useCallback(() => {
    dispatch(hideModal());
  }, [dispatch]);

  const handleSave = useCallback(() => {
    // Validate tags have valid data before dispatching
    const validTags = tags.filter(
      (tag) => tag.value && tag.value.trim().length > 0
    );
    if (validTags.length === 0) return;
    dispatch(applyBulkTags(assets, validTags));
  }, [dispatch, assets, tags]);

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[480px] p-0" showCloseButton={false}>
        <div className="flex flex-col">
          {/* Header */}
          <DialogHeader className="px-6 py-3">
            <DialogTitle className="font-gotham text-lg font-semibold text-neutral-900">
              Add Asset Tags
            </DialogTitle>
            <DialogDescription className="font-inter text-sm text-neutral-600 mt-2">
              Add tags to {assets.length} selected{" "}
              {assets.length === 1 ? "asset" : "assets"}.
            </DialogDescription>
          </DialogHeader>

          {/* Content */}
          <div className="px-6 pb-6 space-y-4">
            <div className="space-y-1.5">
              <label className="font-gotham text-sm font-semibold text-neutral-700">
                Tags
              </label>
              <CreatableSelect
                options={allTags}
                value={tags}
                onChange={setTags}
                placeholder="Tag your asset..."
                createLabel='Create tag "{search}"'
                className="w-full"
              />
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="flex flex-row items-center justify-between sm:justify-between border-t border-neutral-200 px-6 py-3">
            <Button
              variant="ghost"
              onClick={handleClose}
              className="text-neutral-600 hover:text-neutral-700 hover:bg-neutral-100"
            >
              Cancel
            </Button>
            <Button
              disabled={tags.length === 0}
              onClick={handleSave}
              variant="blue-primary"
            >
              Save
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkAssetTagsModal;
